import { useAtom } from "@dbeining/react-atom";
import { Field, Form, Formik } from "formik";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { userAtom } from "../../atoms";
import { useViewport } from "../../contexts";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { ConfirmationModel } from "../../components/features/common";
import {
    EditFormLifecycleActions,
    EditFormSaveCancelButtons,
    EditFormSplitFooter,
    shouldShowEditFormLifecycleActions,
} from "../../components/Forms";
import { RecordAuditMetaBar } from "../../components/Layout/RecordAuditMetaBar";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { EllipsisActionMenu } from "../../components/UI/EllipsisActionMenu";
import { isAllowedVehicleType, VEHICLE_TYPES } from "../../constants/vehicle/vehicleTypes";
import type {
    IVehicleDetail,
    VehicleCreatePayload,
    VehicleUpdatePayload,
} from "../../customTypings/vehicle/Vehicle";
import { vehiclesService } from "../../services/vehicles/vehicles.service";
import { useVehicleRestoreOnCreateConflict } from "../../hooks/useVehicleRestoreOnCreateConflict";
import { createEditFormSubmitHandler, DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE, emptyStringToNull, nullToEmptyString, optionalNullableString, shouldShowFormFieldError, arePayloadsEqual, editFormBreadcrumbLabel, editPageTitle, EntityLabels, ListPageTitles, SectionLabels, getUserFacingErrorMessage, isUnchangedUpdateResponse, loadingPageTitle, logErrorForDev, NO_CHANGES_TOAST_MESSAGE, showToastError, showToastSuccess, showToastUnchanged, buildRecordLifecycleConfirmProps, appRoutes } from "../../utils";
import {
    buildVehicleAdminPagedListActions,
    canMutateVehicles,
    formatVehicleToastLabel,
    getVehicleAdminActionSuccessMessage,
    type VehicleAdminConfirmKind,
} from "../VehiclesListPage/vehicleAdminListActions";

dayjs.extend(advancedFormat);
dayjs.extend(utc);

function formatVehicleMetaDate(iso: string | null | undefined): string {
    if (!iso) return "";
    return dayjs.utc(iso).local().format("Do MMMM YYYY [at] HH:mm");
}

function formatByLine(name: string | null | undefined): string {
    const t = typeof name === "string" ? name.trim() : "";
    return t.length > 0 ? t : "Unknown";
}

type FormValues = VehicleCreatePayload & { id?: string };

function buildVehicleEditValidationSchema(isNew: boolean) {
    const vehicleTypeField = Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .test("vehicle-type", "Select a valid vehicle type.", (v) => isAllowedVehicleType(v));

    return Yup.object({
        id: Yup.string().optional(),
        registration: Yup.string()
            .transform((v) => (typeof v === "string" ? v.trim().toUpperCase() : v))
            .required("Registration is required.")
            .max(32, "Registration must be at most 32 characters."),
        vehicleType: isNew
            ? vehicleTypeField.optional().default("")
            : vehicleTypeField.required("Vehicle type is required."),
        notes: optionalNullableString,
        isEnabled: Yup.boolean().required(),
    });
}

type ConfirmKind = VehicleAdminConfirmKind | null;

const emptyForm: FormValues = {
    registration: "",
    vehicleType: "",
    notes: "",
    isEnabled: true,
};

const VehicleEditPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const currentUser = useAtom(userAtom);
    const roleNames = currentUser?.identity.roles.map((r) => r.name) ?? [];
    const canMutate = canMutateVehicles(roleNames);

    const isNew = !id || id === "new";

    const [loading, setLoading] = useState(!isNew);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [initialValues, setInitialValues] = useState<FormValues>(emptyForm);
    const [metaEnabled, setMetaEnabled] = useState(true);
    const [metaDeleted, setMetaDeleted] = useState(false);
    const [createdAt, setCreatedAt] = useState<string | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [createdByDisplayName, setCreatedByDisplayName] = useState<string | null>(null);
    const [lastUpdatedByDisplayName, setLastUpdatedByDisplayName] = useState<string | null>(null);
    const [deletedAuditAtUtc, setDeletedAuditAtUtc] = useState<string | null>(null);
    const [deletedByDisplayName, setDeletedByDisplayName] = useState<string | null>(null);
    const [disabledAuditAtUtc, setDisabledAuditAtUtc] = useState<string | null>(null);
    const [disabledByDisplayName, setDisabledByDisplayName] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);
    const [legacyVehicleType, setLegacyVehicleType] = useState<string | null>(null);
    const savedUpdatePayloadRef = useRef<VehicleUpdatePayload | null>(null);

    const validationSchema = useMemo(() => buildVehicleEditValidationSchema(isNew), [isNew]);

    const restoreOnCreate = useVehicleRestoreOnCreateConflict({
        restoreDeletedWithCreateValues: vehiclesService.restoreDeletedWithCreateValues,
        onRestored: (detail) => navigate(`/vehicles/${detail.id}`, { replace: true }),
    });

    const headerMenuItem = useMemo(
        () => ({ isEnabled: metaEnabled, isDeleted: metaDeleted }),
        [metaEnabled, metaDeleted]
    );

    const headerMenuActions = useMemo(
        () => buildVehicleAdminPagedListActions((kind) => setConfirmKind(kind)),
        []
    );

    const buildUpdatePayload = (values: FormValues): VehicleUpdatePayload => ({
        id: values.id!,
        vehicleType: emptyStringToNull(values.vehicleType) ?? "",
        notes: emptyStringToNull(values.notes),
    });

    const buildUpdatePayloadFromDetail = (vehicle: IVehicleDetail): VehicleUpdatePayload => {
        const rawType = vehicle.vehicleType ?? "";
        const typeValid = isAllowedVehicleType(rawType);
        return buildUpdatePayload({
            id: vehicle.id,
            registration: vehicle.registration.toUpperCase(),
            vehicleType: typeValid ? rawType.trim() : "",
            notes: nullToEmptyString(vehicle.notes),
            isEnabled: vehicle.isEnabled,
        });
    };

    const applyVehicleDetail = (vehicle: IVehicleDetail) => {
        const rawType = vehicle.vehicleType ?? "";
        const typeValid = isAllowedVehicleType(rawType);
        setLegacyVehicleType(!typeValid && rawType.trim() !== "" ? rawType.trim() : null);
        setInitialValues({
            id: vehicle.id,
            registration: vehicle.registration.toUpperCase(),
            vehicleType: typeValid ? rawType.trim() : "",
            notes: nullToEmptyString(vehicle.notes),
            isEnabled: vehicle.isEnabled,
        });
        setMetaEnabled(vehicle.isEnabled);
        setMetaDeleted(vehicle.isDeleted);
        setCreatedAt(vehicle.createdAtUtc);
        setUpdatedAt(vehicle.updatedAtUtc);
        setCreatedByDisplayName(vehicle.createdByDisplayName ?? null);
        setLastUpdatedByDisplayName(vehicle.lastUpdatedByDisplayName ?? null);
        setDeletedAuditAtUtc(vehicle.deletedAuditAtUtc ?? null);
        setDeletedByDisplayName(vehicle.deletedByDisplayName ?? null);
        setDisabledAuditAtUtc(vehicle.disabledAuditAtUtc ?? null);
        setDisabledByDisplayName(vehicle.disabledByDisplayName ?? null);
        savedUpdatePayloadRef.current = buildUpdatePayloadFromDetail(vehicle);
    };

    useEffect(() => {
        if (isNew || !id) {
            setLoading(false);
            setInitialValues(emptyForm);
            setLegacyVehicleType(null);
            return;
        }
        setLoading(true);
        setLoadError(null);
        vehiclesService
            .getById(id)
            .then(applyVehicleDetail)
            .catch((e: unknown) => {
                logErrorForDev("VehicleEditPage.load", e);
                setLoadError(getUserFacingErrorMessage(e, "Could not load vehicle."));
            })
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.vehicle,
                initialValues.registration || `this ${EntityLabels.vehicle.toLowerCase()}`
            ),
        [confirmKind, initialValues.registration]
    );

    const runMutation = (kind: ConfirmKind) => {
        if (!id || isNew || !kind) return;
        const op =
            kind === "disable"
                ? vehiclesService.disable(id)
                : kind === "enable"
                  ? vehiclesService.enable(id)
                  : kind === "softDelete"
                    ? vehiclesService.softDelete(id)
                    : vehiclesService.restore(id);
        op.then(() => {
            setConfirmKind(null);
            showToastSuccess(getVehicleAdminActionSuccessMessage(kind, initialValues.registration));
            if (kind === "softDelete") {
                navigate("/vehicles", { replace: true });
                return;
            }
            return vehiclesService.getById(id).then(applyVehicleDetail);
        }).catch(() => setConfirmKind(null));
    };

    if (!canMutate) {
        return (
            <Container fluid className="my-3">
                <Alert variant="warning">You do not have permission to manage vehicles.</Alert>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container fluid className="my-5 d-flex justify-content-center">
                <LoadingSpinner text={loadingPageTitle(EntityLabels.vehicle)} />
            </Container>
        );
    }

    if (loadError) {
        return (
            <Container fluid className="my-3">
                <Alert variant="danger">{loadError}</Alert>
                <Link to="/vehicles">Back to vehicles</Link>
            </Container>
        );
    }

    return (
        <Container fluid>
            <Helmet>
                <title>{editPageTitle(EntityLabels.vehicle, isNew)}</title>
            </Helmet>
            <ResponsiveBreadcrumb
                className="mb-3"
                items={[
                    { text: SectionLabels.movementParties, to: appRoutes.transportParties },
                    { text: ListPageTitles.vehicles, to: "/vehicles" },
                    { text: editFormBreadcrumbLabel(isNew) },
                ]}
            />

            <div
                className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100"
                style={{ borderBottom: "2px solid var(--primary)", paddingBottom: "0.5rem" }}
            >
                <h1 className="mb-0 flex-grow-1 min-w-0" style={{ borderBottom: "none", paddingBottom: 0 }}>
                    {editPageTitle(EntityLabels.vehicle, isNew)}
                </h1>
                {isNarrowScreen && !isNew && Boolean(initialValues.id?.trim()) && (
                    <EllipsisActionMenu
                        id="vehicle-edit-actions-dropdown"
                        ariaLabel="Vehicle actions"
                        actions={headerMenuActions}
                        item={headerMenuItem}
                    />
                )}
            </div>

            {!isNew && (
                <RecordAuditMetaBar
                    formatDate={formatVehicleMetaDate}
                    formatByLine={formatByLine}
                    createdAt={createdAt}
                    createdByDisplayName={createdByDisplayName}
                    updatedAt={updatedAt}
                    lastUpdatedByDisplayName={lastUpdatedByDisplayName}
                    isDeleted={metaDeleted}
                    isEnabled={metaEnabled}
                    deletedAuditAtUtc={deletedAuditAtUtc}
                    deletedByDisplayName={deletedByDisplayName}
                    disabledAuditAtUtc={disabledAuditAtUtc}
                    disabledByDisplayName={disabledByDisplayName}
                />
            )}

            <Formik<FormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur
                onSubmit={async (values) => {
                    if (metaDeleted) return;
                    setLiveValidationAfterSubmit(true);
                    try {
                        const t = (s: string) => (typeof s === "string" ? s.trim() : s);
                        const base: VehicleCreatePayload = {
                            registration: t(values.registration),
                            vehicleType: emptyStringToNull(values.vehicleType) ?? "",
                            notes: emptyStringToNull(values.notes),
                            isEnabled: values.isEnabled,
                        };
                        if (isNew) {
                            try {
                                const created = await vehiclesService.create(base);
                                setLiveValidationAfterSubmit(false);
                                showToastSuccess(`${formatVehicleToastLabel(base.registration)} created successfully!`);
                                navigate(`/vehicles/${created.id}`, { replace: true });
                            } catch (err: unknown) {
                                if (restoreOnCreate.handleCreateError(err)) {
                                    restoreOnCreate.setPendingRestoreForm(base);
                                    setLiveValidationAfterSubmit(false);
                                    return;
                                }
                                showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                            }
                        } else if (values.id) {
                            const updatePayload = buildUpdatePayload(values);
                            if (arePayloadsEqual(updatePayload, savedUpdatePayloadRef.current)) {
                                setLiveValidationAfterSubmit(false);
                                showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                                return;
                            }
                            const result = await vehiclesService.update(updatePayload);
                            if (isUnchangedUpdateResponse(result)) {
                                setLiveValidationAfterSubmit(false);
                                showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                                return;
                            }
                            setLiveValidationAfterSubmit(false);
                            showToastSuccess(`${formatVehicleToastLabel(base.registration)} updated successfully!`);
                            navigate("/vehicles");
                        }
                    } catch {
                        showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    }
                }}
            >
                {({ isSubmitting, values, errors, submitCount, setFieldValue, handleSubmit, validateForm, setTouched }) => {
                    const hasPersistedRecord = Boolean(values.id?.trim());
                    const showErr = (field: keyof FormValues) =>
                        shouldShowFormFieldError(
                            errors,
                            field as keyof typeof errors,
                            hasPersistedRecord,
                            submitCount,
                            liveValidationAfterSubmit
                        );
                    return (
                        <Form
                            noValidate
                            onSubmit={createEditFormSubmitHandler(validateForm, setTouched, values, handleSubmit)}
                        >
                            {metaDeleted && (
                                <Alert variant="warning" className="mb-3">
                                    This vehicle is deleted. Restore it before editing its details.
                                </Alert>
                            )}
                            {legacyVehicleType && (
                                <Alert variant="warning" className="mb-3">
                                    Previous vehicle type &quot;{legacyVehicleType}&quot; is not in the current list.
                                    Select a type below before saving.
                                </Alert>
                            )}

                            <Row className="g-3">
                                <Col md={6}>
                                    <label className="form-label" htmlFor="registration">
                                        Registration{" "}
                                        {!isNew ? null : (
                                            <>
                                                <span className="text-danger" aria-hidden="true">*</span>
                                                <span className="visually-hidden"> (required)</span>
                                            </>
                                        )}
                                    </label>
                                    <Field
                                        id="registration"
                                        name="registration"
                                        className={`form-control font-monospace text-uppercase${showErr("registration") ? " is-invalid" : ""}`}
                                        disabled={metaDeleted || !isNew}
                                        readOnly={!isNew}
                                        autoFocus={isNew}
                                        aria-required={isNew}
                                        aria-invalid={showErr("registration")}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            if (!isNew) return;
                                            void setFieldValue("registration", e.target.value.toUpperCase());
                                        }}
                                    />
                                    {!isNew && (
                                        <div className="form-text">Registration cannot be changed after create.</div>
                                    )}
                                    {showErr("registration") && (
                                        <div className="invalid-feedback d-block">{errors.registration}</div>
                                    )}
                                </Col>
                                <Col md={6}>
                                    <label className="form-label" htmlFor="vehicleType">
                                        Vehicle type
                                    </label>
                                    <Field
                                        id="vehicleType"
                                        name="vehicleType"
                                        as="select"
                                        className={`form-select${showErr("vehicleType") ? " is-invalid" : ""}`}
                                        disabled={metaDeleted}
                                        aria-required={!isNew}
                                        aria-invalid={showErr("vehicleType")}
                                    >
                                        {!values.vehicleType && (
                                            <option value="" disabled>
                                                {isNew ? "\u00a0" : "Select vehicle type"}
                                            </option>
                                        )}
                                        {VEHICLE_TYPES.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </Field>
                                    {showErr("vehicleType") && (
                                        <div className="invalid-feedback d-block">{errors.vehicleType}</div>
                                    )}
                                </Col>
                                <Col xs={12}>
                                    <label className="form-label" htmlFor="notes">
                                        Notes
                                    </label>
                                    <Field
                                        id="notes"
                                        name="notes"
                                        as="textarea"
                                        rows={3}
                                        className="form-control"
                                        disabled={metaDeleted}
                                    />
                                </Col>
                                {isNew && (
                                    <Col md={6} className="d-flex align-items-end">
                                        <div className="form-check">
                                            <Field
                                                id="isEnabled"
                                                name="isEnabled"
                                                type="checkbox"
                                                className="form-check-input"
                                            />
                                            <label className="form-check-label" htmlFor="isEnabled">
                                                Available in lists
                                            </label>
                                        </div>
                                    </Col>
                                )}
                            </Row>

                            <EditFormSplitFooter
                                start={
                                    shouldShowEditFormLifecycleActions(isNew, values.id, isNarrowScreen) ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            <EditFormLifecycleActions
                                                metaDeleted={metaDeleted}
                                                metaEnabled={metaEnabled}
                                                onAction={setConfirmKind}
                                            />
                                        </div>
                                    ) : undefined
                                }
                            >
                                <EditFormSaveCancelButtons
                                    isSubmitting={isSubmitting}
                                    disabled={metaDeleted}
                                    saveLabel={isNew ? "Create" : "Save"}
                                    onCancel={() => navigate("/vehicles")}
                                />
                            </EditFormSplitFooter>
                        </Form>
                    );
                }}
            </Formik>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runMutation(confirmKind);
                    else setConfirmKind(null);
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />

            <ConfirmationModel
                show={restoreOnCreate.restoreModalProps.show}
                onClose={(confirmed) => restoreOnCreate.confirmRestore(confirmed)}
                title={restoreOnCreate.restoreModalProps.title}
                body={restoreOnCreate.restoreModalProps.body}
                buttonSuccessText={restoreOnCreate.restoreModalProps.buttonSuccessText}
                buttonCancelText={restoreOnCreate.restoreModalProps.buttonCancelText}
            />
        </Container>
    );
};

export { VehicleEditPage };
